import React, { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusIcon, LinkIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import {
  SEED_EXPENSE_ROWS,
  suggestEssential,
  rowsFromParseResponse,
  computeTotals,
  initEmployers,
  PAY_CYCLE_OPTIONS,
} from './cashflowEntryUtils';

const FREQUENCIES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi_weekly', label: 'Bi-weekly' },
  { value: 'semi_monthly', label: 'Twice a month' },
  { value: 'monthly', label: 'Monthly' },
];

const newRowId = () => `custom-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

/**
 * Stage 1/2 entry: form-first with optional "Describe it instead" prompt mode.
 * Nothing is persisted until explicit Save.
 */
const CashflowEntryForm = ({
  initialIncome = null,
  initialRows = null,
  tagOverrides: initialOverrides = {},
  onSaved,
  onConnectBank,
  plaidLoading = false,
  embedded = false,
}) => {
  const [mode, setMode] = useState('form');
  const [employers, setEmployers] = useState(() => initEmployers(initialIncome));
  const [payCycleLabel, setPayCycleLabel] = useState(initialIncome?.pay_cycle_label || '');
  const [nextPayDate, setNextPayDate] = useState(() => {
    const d = initialIncome?.next_pay_date;
    if (!d) return '';
    return String(d).slice(0, 10);
  });
  const [rows, setRows] = useState(
    initialRows?.length
      ? initialRows.map((r) => ({
          id: r.id || newRowId(),
          label: r.label,
          amount: r.amount != null ? String(r.amount) : '',
          essential: r.essential ?? true,
        }))
      : SEED_EXPENSE_ROWS.map((r) => ({ ...r }))
  );
  const [tagOverrides, setTagOverrides] = useState({ ...initialOverrides });
  const [promptText, setPromptText] = useState('');
  const [promptFile, setPromptFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [parseNote, setParseNote] = useState('');

  const totals = useMemo(() => computeTotals(employers, rows), [employers, rows]);

  const addEmployer = () => {
    setEmployers((prev) => [
      ...prev,
      { id: newRowId(), employer: '', amount: '', frequency: 'monthly' },
    ]);
  };

  const updateEmployer = (id, field, value) => {
    setEmployers((prev) => prev.map((e) => (e.id === id ? { ...e, [field]: value } : e)));
  };

  const toggleEssential = useCallback((rowId, label, next) => {
    setRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, essential: next } : r))
    );
    setTagOverrides((o) => ({ ...o, [label]: next }));
  }, []);

  const updateRow = (rowId, field, value) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== rowId) return r;
        const next = { ...r, [field]: value };
        if (field === 'label') {
          next.essential = suggestEssential(value, { ...tagOverrides, [r.label]: r.essential });
        }
        return next;
      })
    );
  };

  const addCustomRow = () => {
    const label = 'Custom expense';
    setRows((prev) => [
      ...prev,
      {
        id: newRowId(),
        label,
        amount: '',
        essential: suggestEssential(label, tagOverrides),
      },
    ]);
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => {
        const result = r.result;
        resolve(typeof result === 'string' ? result.split(',')[1] : null);
      };
      r.onerror = reject;
      r.readAsDataURL(file);
    });

  const handleParse = async () => {
    const text = promptText.trim();
    if (!text && !promptFile) return;
    setParsing(true);
    setError('');
    setParseNote('');
    try {
      const payload = {};
      if (text) payload.content = text;
      if (promptFile) payload.image_base64 = await fileToBase64(promptFile);
      const res = await axios.post('/api/cashflow/entry/parse', payload);
      if (res.data.income?.sources?.length) {
        setEmployers(
          res.data.income.sources.map((s, i) => ({
            id: `parsed-emp-${i}`,
            employer: s.employer || '',
            amount: String(s.amount ?? ''),
            frequency: s.frequency || 'monthly',
          }))
        );
      } else if (res.data.income?.amount) {
        setEmployers([
          {
            id: 'parsed-emp-0',
            employer: 'Primary income',
            amount: String(res.data.income.amount),
            frequency: res.data.income.frequency || 'monthly',
          },
        ]);
      }
      const parsedRows = rowsFromParseResponse(res.data, tagOverrides);
      if (parsedRows?.length) {
        setRows(parsedRows);
        setParseNote('Review the amounts and tags below, then Save when ready.');
        setMode('form');
      } else {
        setParseNote('Could not extract line items — try listing amounts (e.g. rent $1800, dining $400).');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not parse that description.');
    } finally {
      setParsing(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const sources = employers
        .filter((e) => parseFloat(e.amount) > 0)
        .map((e) => ({
          employer: (e.employer || 'Income').trim() || 'Income',
          amount: parseFloat(e.amount),
          frequency: e.frequency || 'monthly',
        }));
      const income = sources.length
        ? {
            sources,
            pay_cycle_label: payCycleLabel || undefined,
            next_pay_date: nextPayDate || undefined,
          }
        : null;
      const payloadRows = rows
        .filter((r) => r.label.trim() && parseFloat(r.amount) > 0)
        .map((r) => ({
          label: r.label.trim(),
          amount: parseFloat(r.amount),
          essential: r.essential,
        }));
      if (!income && payloadRows.length === 0) {
        setError('Add your income and/or at least one expense amount.');
        setSaving(false);
        return;
      }
      await axios.post('/api/cashflow/entry/save', {
        income,
        rows: payloadRows,
        tag_overrides: tagOverrides,
      });
      onSaved?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      className={`${embedded ? '' : 'max-w-2xl mx-auto'}`}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      {!embedded && (
        <div className="mb-8 pb-6 border-b border-hairline">
          <h2 className="font-display text-h1 text-ink mb-2">Add your money picture</h2>
          <p className="text-body text-ink-muted max-w-md">
            A quick snapshot is enough — fill what you know now. Charts and Aura unlock after you save.
          </p>
        </div>
      )}

      <div className="flex gap-4 mb-6 border-b border-hairline">
        <button
          type="button"
          onClick={() => setMode('form')}
          className={`pb-2 text-body font-medium transition-colors duration-300 ${
            mode === 'form' ? 'nav-tab-active' : 'nav-tab'
          }`}
        >
          Quick form
        </button>
        <button
          type="button"
          onClick={() => setMode('prompt')}
          className={`pb-2 text-body font-medium transition-colors duration-300 ${
            mode === 'prompt' ? 'nav-tab-active' : 'nav-tab'
          }`}
        >
          Describe it instead
        </button>
      </div>

      <AnimatePresence mode="wait">
        {mode === 'prompt' ? (
          <motion.div
            key="prompt"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="mb-6"
          >
            <p className="text-label text-ink-muted mb-2">
              Aura will fill the form below for you to review — nothing is saved until you hit Save.
            </p>
            <textarea
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              placeholder="e.g. I make $6k/month; rent $1800; groceries $500; dining about $400"
              className="input-editorial resize-none mb-2"
              rows={4}
              disabled={parsing}
            />
            <label className="inline-block px-3 py-1.5 mb-3 border border-hairline rounded-md text-body text-ink cursor-pointer hover:bg-pine-tint">
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => setPromptFile(e.target.files?.[0] || null)}
              />
              {promptFile ? promptFile.name : 'Upload screenshot (optional)'}
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleParse}
                disabled={parsing || (!promptText.trim() && !promptFile)}
                className="btn-primary text-label disabled:opacity-50"
              >
                {parsing ? 'Parsing…' : 'Fill the form'}
              </button>
              <button
                type="button"
                onClick={() => setMode('form')}
                className="btn-ghost text-label"
              >
                Back to form
              </button>
            </div>
            {parseNote && <p className="text-body text-pine mt-2">{parseNote}</p>}
          </motion.div>
        ) : null}
      </AnimatePresence>

      <div className="space-y-4 mb-4">
        <div>
          <label className="text-label text-ink-muted">
            Income (one or more employers)
          </label>
          <p className="text-label text-ink-faint mt-0.5 mb-2">
            Per pay period for each job — we estimate your monthly total and pay-cycle progress.
          </p>
          <div className="space-y-2">
            {employers.map((emp) => (
              <div
                key={emp.id}
                className="flex flex-wrap items-center gap-2 py-2 border-b border-hairline"
              >
                <input
                  type="text"
                  value={emp.employer}
                  onChange={(e) => updateEmployer(emp.id, 'employer', e.target.value)}
                  placeholder="Employer name"
                  className="flex-1 min-w-[100px] input-editorial py-1.5"
                />
                <div className="relative w-28">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-ink-faint text-label figure">$</span>
                  <input
                    type="number"
                    min="0"
                    value={emp.amount}
                    onChange={(e) => updateEmployer(emp.id, 'amount', e.target.value)}
                    placeholder="Pay"
                    className="w-full pl-5 pr-1 py-1.5 input-editorial figure"
                  />
                </div>
                <select
                  value={emp.frequency}
                  onChange={(e) => updateEmployer(emp.id, 'frequency', e.target.value)}
                  className="px-2 py-1.5 input-editorial text-label"
                >
                  {FREQUENCIES.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addEmployer}
            className="mt-2 flex items-center gap-1 text-body text-pine hover:text-pine-deep"
          >
            <PlusIcon className="w-4 h-4" />
            Add another employer
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-hairline">
          <div>
            <label className="text-label text-ink-muted">Pay cycle</label>
            <select
              value={payCycleLabel}
              onChange={(e) => setPayCycleLabel(e.target.value)}
              className="mt-1 w-full input-editorial"
            >
              {PAY_CYCLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-label text-ink-muted">Next payday (optional)</label>
            <input
              type="date"
              value={nextPayDate}
              onChange={(e) => setNextPayDate(e.target.value)}
              className="mt-1 w-full input-editorial"
            />
          </div>
        </div>

        <div className="pt-4 border-t border-hairline">
          <label className="text-label text-ink-muted">Expenses</label>
          <p className="text-label text-ink-faint mt-0.5 mb-2">
            Tap the tag to switch essential ↔ non-essential. You can add more anytime.
          </p>
          <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
            {rows.map((row) => (
              <div
                key={row.id}
                className="flex flex-wrap items-center gap-2 py-2 border-b border-hairline last:border-0"
              >
                <input
                  type="text"
                  value={row.label}
                  onChange={(e) => updateRow(row.id, 'label', e.target.value)}
                  className="flex-1 min-w-[120px] input-editorial py-1.5"
                />
                <div className="relative w-28">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-ink-faint text-label figure">$</span>
                  <input
                    type="number"
                    min="0"
                    value={row.amount}
                    onChange={(e) => updateRow(row.id, 'amount', e.target.value)}
                    placeholder="0"
                    className="w-full pl-5 pr-1 py-1.5 input-editorial figure"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => toggleEssential(row.id, row.label, !row.essential)}
                  className={`shrink-0 px-2 py-1 rounded-md text-label font-medium border transition-colors duration-300 ${
                    row.essential
                      ? 'border-pine bg-pine-tint text-pine'
                      : 'border-hairline text-ink-muted hover:bg-pine-tint'
                  }`}
                  title="Tap to change"
                >
                  {row.essential ? 'Essential' : 'Non-ess.'}
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addCustomRow}
            className="mt-2 flex items-center gap-1 text-body text-pine hover:text-pine-deep"
          >
            <PlusIcon className="w-4 h-4" />
            Add custom expense
          </button>
        </div>
      </div>

      {(totals.essential > 0 || totals.discretionary > 0 || totals.income > 0) && (
        <div className="mb-6 py-4 border-t border-b border-hairline text-label text-ink-muted flex flex-wrap gap-6 justify-center figure">
          {totals.income > 0 && (
            <span>Est. monthly income: ${totals.income.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
          )}
          {totals.essential > 0 && <span>Essentials: ${totals.essential.toLocaleString()}</span>}
          {totals.discretionary > 0 && (
            <span>Lifestyle: ${totals.discretionary.toLocaleString()}</span>
          )}
        </div>
      )}

      {error && <p className="text-danger text-body mb-3 text-center">{error}</p>}

      <div className="flex flex-wrap items-center justify-center gap-3 pt-4">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="btn-primary disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        {onConnectBank && (
          <button
            type="button"
            onClick={onConnectBank}
            disabled={plaidLoading}
            className="btn-secondary text-label disabled:opacity-50"
          >
            <LinkIcon className="w-4 h-4" />
            {plaidLoading ? 'Connecting…' : 'Connect bank'}
          </button>
        )}
      </div>
    </motion.div>
  );
};

export default CashflowEntryForm;
